import './SourcePicker.scss';
import { ReactElement, useState, useMemo, useEffect, useCallback } from 'react';
import { Dropdown, MenuItem } from '@Components/Dropdown';
import { Placement, Size, SizeType } from '@Components/designConstants';
import { invalidAnalyticsDetails } from '@src/utils/columnMetricSystem/configUtils';

import { Warning800 } from '@src/utils/colors';

export const SourceType = {
  PLATFORM: 'platform',
  ANALYTICS: 'analytics',
  GOOGLE_ADS_HUB: 'google_ads_data_hub'
} as const;
export type SourceTypeValue = (typeof SourceType)[keyof typeof SourceType];

export const SOURCE_TYPE_LABELS: Record<SourceTypeValue, string> = {
  [SourceType.PLATFORM]: 'Platform',
  [SourceType.ANALYTICS]: 'Source of Truth',
  [SourceType.GOOGLE_ADS_HUB]: 'Google ADH'
};

const SOURCE_TYPES: SourceTypeValue[] = [
  SourceType.PLATFORM,
  SourceType.ANALYTICS,
  SourceType.GOOGLE_ADS_HUB
];

const isSourceType = (value: string): value is SourceTypeValue =>
  SOURCE_TYPES.includes(value as SourceTypeValue);

// Maps a flat list of selected source values to a SourceType.
// ADH is only considered when it exists in the available options.
// An empty or platform-only selection is treated as Platform.
export const deriveSourceType = (selectedSources: string[]): SourceTypeValue => {
  if (selectedSources.length === 1) {
    if (selectedSources[0] === SourceType.GOOGLE_ADS_HUB) {
      return SourceType.GOOGLE_ADS_HUB;
    }
    if (selectedSources[0] === SourceType.PLATFORM) {
      return SourceType.PLATFORM;
    }
  } else if (
    selectedSources.includes(SourceType.GOOGLE_ADS_HUB) ||
    selectedSources.includes(SourceType.PLATFORM)
  ) {
    throw Error('cannot combine platform or google ads data hub with other sources');
  }
  return SourceType.ANALYTICS;
};

interface SourcePickerProps {
  applyOnClick: (sources: string[]) => void;
  platforms: string[];
  selectedSources: string[];
  sourceOptions: { label: string; value: string }[];
  disabled?: boolean;
  singleSelect?: boolean;
  invalidAnalyticDetailsTable?: invalidAnalyticsDetails;
  invalidAnalyticDetailsFilter?: invalidAnalyticsDetails;
  size?: SizeType;
}

/*
 * If platform is the selected source type, there won't be a selected value because you don't select a specific platform.
 * If Source of Truth is selected, you will select a specific value if you're on the Overview tab, but no selected value when on the other tabs.
 * When in a state where there isn't a selected value, we use the menuRenderOverride to display a string list of options instead of a picker.
 */
export const SourcePicker = ({
  applyOnClick,
  platforms,
  selectedSources,
  sourceOptions,
  disabled,
  singleSelect = false,
  invalidAnalyticDetailsTable,
  invalidAnalyticDetailsFilter,
  size = Size.LG
}: SourcePickerProps) => {
  // Left and right panel state are intentionally separate:
  // localType drives which left item is checked and which right options are shown.
  // localValues holds the selected SoT values; it is preserved across type switches
  // so switching Platform → SoT → Platform → SoT restores the prior SoT selection.
  const [localType, setLocalType] = useState<SourceTypeValue>(() =>
    deriveSourceType(selectedSources)
  );
  const [localValues, setLocalValues] = useState<string[]>(() => {
    const type = deriveSourceType(selectedSources);
    return type === SourceType.ANALYTICS ? selectedSources : [];
  });

  useEffect(() => {
    const type = deriveSourceType(selectedSources);
    setLocalType(type);
    setLocalValues(type === SourceType.ANALYTICS ? selectedSources : []);
  }, [selectedSources, sourceOptions]);

  // Pre-categorize the flat API list once so all memos below avoid repeated filtering.
  const sotOptions = useMemo(
    () =>
      sourceOptions.filter(
        o => o.value !== SourceType.PLATFORM && o.value !== SourceType.GOOGLE_ADS_HUB
      ),
    [sourceOptions]
  );

  const adhOption = useMemo(
    () => sourceOptions.find(o => o.value === SourceType.GOOGLE_ADS_HUB),
    [sourceOptions]
  );

  const handleSetSourceValue = useCallback(
    (value: string) => {
      if (singleSelect) {
        setLocalValues(localValues.includes(value) ? [] : [value]);
        return;
      }
      if (localValues.includes(value)) {
        setLocalValues(localValues.filter(s => s !== value));
      } else {
        setLocalValues([...localValues, value]);
      }
    },
    [localValues, singleSelect]
  );
  const sourceTypeOptions = useMemo(() => {
    const options: MenuItem<string>[] = [];
    for (const sourceType of SOURCE_TYPES) {
      // ADH is only shown when the API returned it as an available option
      if (sourceType === SourceType.GOOGLE_ADS_HUB && !adhOption) {
        continue;
      }

      let isDisabled = false;
      let disableByPresets = false;
      let tooltiptext: string | undefined;
      let verticalShift = 55;
      let plural = false;

      if (sourceType === SourceType.ANALYTICS) {
        if (sotOptions.length === 0) {
          isDisabled = true;
        } else {
          if (invalidAnalyticDetailsTable && invalidAnalyticDetailsTable[0]) {
            disableByPresets = true;
            const dimNames = invalidAnalyticDetailsTable[1] || [];
            const dimsPlural = dimNames.length > 1;
            verticalShift = 70;
            if (dimsPlural) {
              plural = true;
              tooltiptext = `Metrics table dimensions ${dimNames.join(', and ')}`;
            } else {
              tooltiptext = `Metrics table dimension ${dimNames.join(', and ')}`;
            }
          }
          if (invalidAnalyticDetailsFilter && invalidAnalyticDetailsFilter[0]) {
            disableByPresets = true;
            if (tooltiptext) {
              tooltiptext = `${tooltiptext} and `;
              verticalShift = 90;
              plural = true;
            }
            const dimNames = invalidAnalyticDetailsFilter[1] || [];
            tooltiptext = `${tooltiptext || ''}Filter with ${dimNames.join(', and ')}`;
          }
          if (disableByPresets && tooltiptext) {
            const msgEnding = plural ? 'are incompatible with SoT' : 'is incompatible with SoT';
            tooltiptext = `${tooltiptext} ${msgEnding}`;
          }
        }
      }

      options.push({
        label: SOURCE_TYPE_LABELS[sourceType],
        value: sourceType,
        selected: localType === sourceType,
        className: isDisabled ? 'disabled' : '',
        disabled: disableByPresets,
        simpleTooltip: tooltiptext,
        simpleTooltipVerticalShift: `-${verticalShift}px`
      });
    }
    return options;
  }, [localType, sotOptions, adhOption, invalidAnalyticDetailsFilter, invalidAnalyticDetailsTable]);

  const rightMenuOptions = useMemo(() => {
    if (localType === SourceType.PLATFORM) {
      return platforms.map(platform => ({
        label: platform,
        value: platform,
        selected: localValues.includes(platform)
      }));
    }
    if (localType === SourceType.GOOGLE_ADS_HUB && adhOption) {
      return [{ label: adhOption.label, value: adhOption.value, selected: true }];
    }
    return sotOptions.map(option => ({
      ...option,
      selected: localValues.includes(option.value),
      section: ['tvad', 'skai'].includes(option.value) ? 'bottom' : 'top'
    }));
  }, [localType, platforms, localValues, sotOptions, adhOption]);

  // Render the right panel as a non-interactive string list for Platform and ADH,
  // since there's no value to select — the source type itself is the full selection.
  const menuRenderOverride = useMemo(() => {
    if (localType === SourceType.PLATFORM || localType === SourceType.GOOGLE_ADS_HUB) {
      return { '1': 'stringList' as const };
    }
    return undefined;
  }, [localType]);

  const multiMenuSections = {
    1: [
      { label: 'top', displayLabel: false },
      { label: 'bottom', displayLabel: false }
    ]
  };

  const menuSectionTitles = useMemo(() => {
    if (localType === SourceType.ANALYTICS && !singleSelect) {
      return {
        1: (
          <div style={{ color: Warning800, fontSize: '12px', padding: '7px 24px' }}>
            Selecting multiple sources can lead to over counting.
          </div>
        )
      };
    }
    return {} as Record<string, ReactElement>;
  }, [localType, singleSelect]);

  const labelValue = useMemo(() => {
    if (singleSelect && localType === SourceType.ANALYTICS) {
      const selectedSotOption = sotOptions.find(o => localValues.includes(o.value));
      return `Source: ${selectedSotOption?.label}`;
    }
    return `Source: ${SOURCE_TYPE_LABELS[localType]}`;
  }, [localType, localValues, sotOptions, singleSelect]);

  const disableApply = useMemo(
    () => localType === SourceType.ANALYTICS && localValues.length === 0,
    [localType, localValues]
  );

  return (
    <div className='cross-channel-source-picker'>
      <Dropdown
        overlayPlacement={Placement.RIGHT}
        size={size}
        menuStrategy='fixed'
        value={labelValue}
        options={[]}
        menuItems={[sourceTypeOptions, rightMenuOptions]}
        hasFilterBar={false}
        // Left column (source type: Platform/SoT/ADH) is always single-choice; the right
        // column (specific values) supports multiple unless singleSelect is requested.
        multiSelect={[false, !singleSelect]}
        multiMenuSections={multiMenuSections}
        menuRenderOverride={menuRenderOverride}
        menuSectionTitles={menuSectionTitles}
        applyDisabled={disableApply}
        applyOnClick={() => {
          if (localType === SourceType.PLATFORM || localType === SourceType.GOOGLE_ADS_HUB) {
            applyOnClick([localType]);
          } else {
            applyOnClick(localValues);
          }
        }}
        cancelOnClick={() => {
          if (disableApply) {
            applyOnClick([SourceType.PLATFORM]);
            setLocalType(SourceType.PLATFORM);
            setLocalValues([]);
          }
        }}
        onChange={(value: string) => {
          if (isSourceType(value)) {
            // Left panel: update the selected type; right panel values are preserved across switches
            setLocalType(value);
          } else {
            handleSetSourceValue(value);
          }
        }}
        disabled={disabled}
      />
    </div>
  );
};
